import { useState } from "react";
import { Link } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, AlertTriangle, ChevronDown, ChevronUp, ExternalLink, HelpCircle } from "lucide-react";

/**
 * Pre-payment Terms acknowledgment gate.
 *
 * Renders a checkbox the patient MUST physically click before they can
 * submit payment. Surfaces a plain-English summary of the most material
 * clauses (pre-ship $25 cancel policy, address responsibility, cold-chain
 * warm-on-arrival) with an expandable section for the rest, plus a deep
 * link to the full Terms of Service page.
 *
 * Includes an inline "Why $25?" tooltip on the consult-fee bullet so the
 * trust signal stays strong at the exact moment a worried patient is
 * reading the fine print.
 *
 * Props:
 *   - checked: bool — controlled checkbox state
 *   - onChange: (newChecked: bool) => void
 */
export default function TermsAcknowledgmentCard({ checked, onChange }) {
  const [expanded, setExpanded] = useState(false);
  const [showWhy25, setShowWhy25] = useState(false);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="terms-ack-card">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
          <ShieldCheck className="w-4 h-4 text-amber-700" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-base font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Before you pay — please confirm you&apos;ve read these
          </p>
          <p className="text-xs text-slate-500 mt-0.5">Three quick things specific to GLP-1 telehealth that aren&apos;t obvious from a typical online order.</p>
        </div>
      </div>

      <ul className="mt-4 space-y-2.5 text-sm text-slate-700">
        <li className="flex gap-2.5">
          <span className="text-emerald-600 shrink-0">●</span>
          <span>
            <b>Cancel before shipping → full refund minus a flat $25 consult fee.</b> Once your order has shipped, all sales are final — compounded medication is custom-made for you and cannot be reused.
            {' '}
            <span className="relative inline-block">
              <button
                type="button"
                onClick={() => setShowWhy25((v) => !v)}
                onBlur={() => setShowWhy25(false)}
                className="inline-flex items-center gap-0.5 text-purple-700 hover:text-purple-900 font-medium underline-offset-2 hover:underline"
                data-testid="why-25-trigger"
                aria-expanded={showWhy25}
              >
                Why $25?
                <HelpCircle className="w-3 h-3" />
              </button>
              {showWhy25 && (
                <span
                  role="tooltip"
                  className="absolute left-0 top-full mt-2 z-20 w-72 rounded-xl bg-slate-900 text-white text-xs leading-relaxed px-3 py-2.5 shadow-xl"
                  data-testid="why-25-tooltip"
                >
                  This $25 covers your provider&apos;s review of your medical questionnaire — which has already happened by the time you ask for a refund. Reaching us <i>before</i> the pharmacy ships is the only way to get a refund (minus this fee). After shipping, the order is final.
                  <span className="absolute -top-1.5 left-3 w-3 h-3 bg-slate-900 rotate-45" />
                </span>
              )}
            </span>
          </span>
        </li>
        <li className="flex gap-2.5">
          <span className="text-red-600 shrink-0">●</span>
          <span><b>Your shipping address is your responsibility.</b> If you enter the wrong address (missing apt #, suite, gate code, or anything else) and the package is lost, returned, or destroyed, you&apos;ll need to pay for a reshipment in full. Update your address from your profile any time, but updates don&apos;t redirect orders already placed.</span>
        </li>
        <li className="flex gap-2.5">
          <span className="text-amber-600 shrink-0">●</span>
          <span><b>Ice packs may melt and the vial may feel warm on arrival.</b> That&apos;s normal. Our independent stability study confirms the medication remains potent and safe after standard ground transit. Warm-on-arrival vials are <b>not eligible for refund or replacement</b>. <Link to="/shipping-study" target="_blank" className="text-purple-700 font-medium underline inline-flex items-center gap-0.5" data-testid="terms-ack-shipping-study-link">View the study <ExternalLink className="w-3 h-3 inline" /></Link></span>
        </li>
        <li className="flex gap-2.5">
          <span className="text-emerald-600 shrink-0">✓</span>
          <span><b>If your provider denies your order entirely</b> (medical denial after reviewing your questionnaire), you receive a <b>full refund — no $25 retained</b>. The fee only applies to patient-side cancellations / declines after a successful provider review.</span>
        </li>
      </ul>

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mt-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
        data-testid="terms-ack-expand"
      >
        {expanded ? <><ChevronUp className="w-3 h-3" /> Hide other terms</> : <><ChevronDown className="w-3 h-3" /> Show other terms</>}
      </button>

      {expanded && (
        <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-4 text-xs text-slate-600 space-y-2">
          <p>You must be 18+ and physically located in a state where our providers are licensed. Prescriptions are subject to provider review and approval. GLPFlo may deny service if a provider determines treatment is not appropriate.</p>
          <p>Subscription tiers auto-renew until paused or cancelled from your dashboard. Cancellation does not refund or credit prior charges.</p>
          <p>If your prescriber recommends a different strength than you requested and you <i>decline</i> the change, the same $25 consultation fee applies (the provider review has already taken place).</p>
          <p>If a vial arrives <b>visibly damaged</b> (broken seal, cracked glass, leak, particulate matter), contact us within 24 hours and we&apos;ll replace it at no charge. This is the only physical-condition exception to the no-refund rule.</p>
          <p>For full legal text, see the <Link to="/terms-of-service" target="_blank" className="text-purple-700 font-medium underline">complete Terms of Service</Link>.</p>
        </div>
      )}

      <label className={`mt-4 flex items-start gap-3 rounded-xl border-2 p-4 cursor-pointer transition-all ${checked ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50/50 hover:bg-amber-50"}`} data-testid="terms-ack-row">
        <Checkbox
          checked={checked}
          onCheckedChange={(v) => onChange(!!v)}
          className="mt-0.5 h-5 w-5 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
          data-testid="terms-ack-checkbox"
        />
        <div className="text-sm">
          <p className={`font-semibold ${checked ? "text-emerald-900" : "text-amber-900"}`}>
            I have read and agree to the GLPFlo Terms of Service.
          </p>
          <p className={`text-xs mt-0.5 ${checked ? "text-emerald-700" : "text-amber-800"}`}>
            Including: <b>refund &amp; $25 consult fee policy</b> (Section 4), <b>shipping-address responsibility</b> (Section 5), and <b>cold-chain warm-on-arrival stability</b> (Section 6).
          </p>
        </div>
      </label>

      {!checked && (
        <p className="mt-2 text-[11px] text-amber-700 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> You must check the box above to continue to payment.
        </p>
      )}
    </div>
  );
}
